import type {TaskJobDetailsRef} from 'src/types/task-and-reminder';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu/Menu';

import * as React from 'react';
import debounce from 'lodash/debounce';
import {useDispatch} from 'react-redux';

import {SimpleTypeahead} from '@spaced-out/ui-design-system/lib/components/Typeahead';

import * as reduxApi from 'src/utils/redux-api-v2';

import {mergeTypeaheadSelectedOption} from 'src/components/task-and-reminder/lib/merge-typeahead-selected-option';


const DEFAULT_JOB_OPTIONS_PAGE_SIZE = 10;

type FilteredJobRow = {
  id: string | number;
  title?: string | null;
};

type FilteredJobsResponse = {
  content?: Array<FilteredJobRow>;
};

type TaskJobSelectFieldProps = {
  label: string;
  placeholder: string;
  value: TaskJobDetailsRef | null;
  onChange: (next: TaskJobDetailsRef | null) => void;
  className?: string;
};

export const TaskJobSelectField = ({
  label,
  placeholder,
  value,
  onChange,
  className,
}: TaskJobSelectFieldProps) => {
  const dispatch = useDispatch();
  const [filteredList, setFilteredList] = React.useState<Array<FilteredJobRow>>(
    [],
  );
  const [isLoading, setIsLoading] = React.useState(false);

  const typeaheadOptions = React.useMemo(
    () =>
      mergeTypeaheadSelectedOption(
        filteredList.map((job) => ({
          key: String(job.id),
          label: job.title || '',
        })),
        value?.id ? {id: String(value.id), label: value.title ?? ''} : null,
      ),
    [filteredList, value?.id, value?.title],
  );

  const selectedKeys = React.useMemo(
    () => (value?.id ? [String(value.id)] : []),
    [value?.id],
  );

  const fetchFilteredList = React.useCallback(
    async (query: string): Promise<Array<FilteredJobRow>> => {
      const response = (await dispatch(
        reduxApi.post(
          `/api/filtered-jobs`,
          {
            jobStatuses: ['OPEN'],
            titleOrCode: query,
            template: false,
            referralActive: false,
            publicAccess: false,
            page: 0,
            pageSize: DEFAULT_JOB_OPTIONS_PAGE_SIZE,
          },
          {},
          {apiPath: '/trmapi'},
        ),
      )) as FilteredJobsResponse | null | undefined;
      const content = response?.content;
      const rows = Array.isArray(content) ? content : [];
      return rows.slice(0, DEFAULT_JOB_OPTIONS_PAGE_SIZE);
    },
    [dispatch],
  );

  const debouncedSearch = React.useMemo(
    () =>
      debounce((query: string) => {
        void (async () => {
          setIsLoading(true);
          try {
            const list = await fetchFilteredList(query);
            setFilteredList(list);
          } finally {
            setIsLoading(false);
          }
        })();
      }, 400),
    [fetchFilteredList],
  );

  React.useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch],
  );

  React.useEffect(() => {
    let cancelled = false;
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchFilteredList('');
        if (!cancelled) {
          setFilteredList(list);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchFilteredList]);

  const handleSelect = (option: MenuOption) => {
    const row = filteredList.find((job) => String(job.id) === option.key);
    const title = row?.title || option.label || '';
    onChange({id: option.key, title});
  };

  const handleClear = () => {
    onChange(null);
    void (async () => {
      setIsLoading(true);
      try {
        const list = await fetchFilteredList('');
        setFilteredList(list);
      } finally {
        setIsLoading(false);
      }
    })();
  };

  return (
    <div className={className}>
      <SimpleTypeahead
        label={label}
        placeholder={placeholder}
        selectedKeys={selectedKeys}
        menuOpenOffset={0}
        onSearch={(evt) => {
          debouncedSearch(evt.target.value);
        }}
        onSelect={handleSelect}
        onClear={handleClear}
        options={typeaheadOptions}
        isLoading={isLoading}
      />
    </div>
  );
};
